import { useState, useEffect, useCallback, useRef } from 'react';
import { postJson, fetchJson } from '../api.js';
import { cachedFetch, bustPrefix } from '../dataCache.js';
import { supabase } from '../supabaseClient.js';
import DataTable from './datatable/DataTable.jsx';
import CertificatesPanel from './CertificatesPanel.jsx';
import SendLogPanel from './SendLogPanel.jsx';
import { fmtMoneyRounded as fmtMoney } from '../utils/money.js';
import { cycleKeyFromDate, currentCycle, iso } from './balances/helpers.js';
import PageHeader from './balances/PageHeader.jsx';
import SubTabs from './balances/SubTabs.jsx';
import PendingReminder from './balances/PendingReminder.jsx';
import QboWarning from './balances/QboWarning.jsx';
import CycleBar from './balances/CycleBar.jsx';
import CustomRangeBar from './balances/CustomRangeBar.jsx';
import BalancesTable from './balances/BalancesTable.jsx';
import ReconPanel from './balances/ReconPanel.jsx';
import StatementsModal from './balances/StatementsModal.jsx';
import CloseCycleModal from './balances/CloseCycleModal.jsx';
import DrillModal from './balances/DrillModal.jsx';
import AdjustmentsModal from './balances/AdjustmentsModal.jsx';
import ExplainModal from './balances/ExplainModal.jsx';

// Balances page: what each customer owes for a statement cycle (1st-15th or
// 16th-end of month) - opening balance + loads billed - QuickBooks invoices
// / payments +/- adjustments = ending balance. Also: cycle close, customer
// statements, compliance certificates and the send log. This file holds the
// page's data and actions; everything visible is drawn by ./balances/.
export default function Balances({ canWrite = true }) {
  // ── Period being viewed (from / to) + the loaded balances data ──
  const [{ from, to }, setRange] = useState(() => currentCycle());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [drill, setDrill] = useState(null);   // { type:'tips'|'invoices', customer, rows }
  const [explain, setExplain] = useState(null); // { customer } -> Explain-the-difference pop-up
  // ── Cycle status (open / closed) and the close-cycle pop-up ──
  // Cycle awareness (status badge + Close button)
  const [cycleInfo, setCycleInfo] = useState(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeNotes, setCloseNotes] = useState('');
  const [closeBusy, setCloseBusy] = useState(false);
  const [me, setMe] = useState(null);
  // ── Reconciliation panel ──
  // Reconciliation panel (collapsible)
  const [recon, setRecon] = useState(null);
  const [reconBusy, setReconBusy] = useState(false);
  const [reconOpen, setReconOpen] = useState(false);
  // ── Sub-tab + statements pop-up ──
  // Sub-tab: 'balances' (default) | 'certificates'. Certificates moved
  // out of the inline collapsible into its own view (Suresh 2026-08-20).
  const [subTab, setSubTab] = useState('balances');
  const [stmtsOpen, setStmtsOpen] = useState(false);
  const [stmtsList, setStmtsList] = useState(null);
  const [stmtsBusy, setStmtsBusy] = useState(false);
  // ── Adjustments pop-up (opened from the Adj $ column) ──
  // Balance adjustments modal — opened from the Adj $ column. Tracks the
  // customer being edited, the list of existing adjustments for that
  // customer in this cycle, and the new-row entry form fields.
  const [adjRow, setAdjRow] = useState(null);          // { customer } when open
  const [adjItems, setAdjItems] = useState([]);        // existing adjustments for that customer
  const [adjBusy, setAdjBusy] = useState(false);
  const [adjAmount, setAdjAmount] = useState('');
  const [adjDescription, setAdjDescription] = useState('');
  // Adjustment date. Defaults to today's local ISO — finance can
  // backdate to land the adjustment in a specific reconciliation
  // window (Suresh 2026-07-08: "let the user pick the date for the
  // adjustment instead of taking it from the date the adjustment was
  // posted"). The backend stores this on posted_at (schema-032).
  const [adjDate, setAdjDate] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
  });
  // ── Custom range filter (date+time precision) ──────────────────────
  // Defaults inherit from the active cycle so users only tweak the
  // boundary they care about. Empty fromTime/toTime = whole-day.
  const [customOpen, setCustomOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(() => currentCycle().from);
  const [customFromTime, setCustomFromTime] = useState('00:00');
  const [customTo, setCustomTo] = useState(() => currentCycle().to);
  const [customToTime, setCustomToTime] = useState('23:59');
  // Active time precision sent on the URL; '' = no time filter (cycle mode).
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  // ── Statement settings: period sent with every statement request + standing CC ──
  // Statement URL params. Suresh 2026-07-07: statements now use the
  // displayed date range (from / to) instead of the fixed cycle_key
  // so custom-range statements are possible. The cycle_key is still
  // sent as a hint (some downstream tracking uses it), but the
  // backend prefers from/to when both are present.
  const stmtRange = 'from=' + encodeURIComponent(from) + '&to=' + encodeURIComponent(to);
  // Standing CC on every outgoing statement (ops copies). Editable in
  // the send modal; persisted server-side via /api/statements/cc.
  const [stmtCc, setStmtCc] = useState('');
  const [stmtCcSaved, setStmtCcSaved] = useState('');
  const [stmtCcBusy, setStmtCcBusy] = useState(false);
  async function saveStmtCc() {
    setStmtCcBusy(true);
    try {
      const emails = stmtCc.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
      const r = await fetch('/api/statements/cc', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails })
      });
      if (!r.ok) throw new Error(await r.text());
      const j = await r.json();
      setStmtCc((j.emails || []).join(', '));
      setStmtCcSaved((j.emails || []).join(', '));
    } catch (e) { alert('CC save failed: ' + e.message); }
    finally { setStmtCcBusy(false); }
  }

  // ── Actions: open statements, adjustments (add / re-date / delete) ──
  async function openStatementsModal() {
    setStmtsOpen(true); setStmtsBusy(true); setStmtsList(null);
    try {
      const j = await fetchJson('/api/statements/generate?list=1&' + stmtRange);
      setStmtsList(j);
    } catch (e) { setStmtsList({ error: e.message }); }
    finally { setStmtsBusy(false); }
    // Best-effort: current standing CC for the editor (non-blocking).
    try {
      const cc = await fetchJson('/api/statements/cc');
      setStmtCc((cc.emails || []).join(', '));
      setStmtCcSaved((cc.emails || []).join(', '));
    } catch (_) {}
  }

  async function openAdjustmentsModal(customer) {
    setAdjRow({ customer });
    setAdjAmount(''); setAdjDescription('');
    setAdjItems([]); setAdjBusy(true);
    try {
      // Query by DATE RANGE, not cycle_key. Pre-schema-032 adjustments
      // have stale cycle_key (defaulted from the row's original cycle
      // at insert time) even though their posted_at now lines up with
      // a different reconciliation window. The Balances tab uses
      // posted_at via balances-recon; the modal must use the same
      // window to show the same set. Suresh 2026-07-08.
      const j = await fetchJson('/api/balance-adjustments?from=' + encodeURIComponent(from)
                              + '&to=' + encodeURIComponent(to)
                              + '&customer=' + encodeURIComponent(customer));
      setAdjItems(j.adjustments || []);
    } catch (e) { setAdjItems([{ _err: e.message }]); }
    finally { setAdjBusy(false); }
  }
  async function saveAdjustment() {
    const amt = Number(adjAmount);
    if (!adjRow || Number.isNaN(amt) || !adjDescription.trim()) return;
    setAdjBusy(true);
    try {
      // Convert the picked YYYY-MM-DD to an ISO with mid-day time so
      // it lands cleanly inside the calendar day regardless of TZ
      // rounding. Backend stores in UTC.
      const postedIso = adjDate ? (adjDate + 'T12:00:00Z') : null;
      // cycle_key is derived server-side from the chosen date, but we
      // still send it as a fallback for pre-schema-032 environments
      // where posted_at doesn't exist. Frontend cycleKey is derived
      // from the currently-viewed period, not the picked date -- so
      // send the date-derived cycle_key so a backdated adjustment
      // lands in the right cycle even in fallback mode.
      const m = adjDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      const derivedCycleKey = m
        ? m[1] + '-' + m[2] + '-' + (parseInt(m[3]) <= 15 ? 'A' : 'B')
        : cycleKey;
      await postJson('/api/balance-adjustments', {
        customer: adjRow.customer,
        cycle_key: derivedCycleKey,
        amount: amt,
        description: adjDescription.trim(),
        posted_at: postedIso
      });
      setAdjAmount(''); setAdjDescription('');
      // Refresh modal list
      // Query by DATE RANGE, not cycle_key. Pre-schema-032 adjustments
      // have stale cycle_key values (defaulted from the row's original
      // cycle at insert time) even though their posted_at now lines up
      // with a different reconciliation window. The Balances tab uses
      // posted_at via balances-recon; the modal must use the same
      // window to show the same set of items. Suresh 2026-07-08.
      const j = await fetchJson('/api/balance-adjustments?from=' + encodeURIComponent(from)
                              + '&to=' + encodeURIComponent(to)
                              + '&customer=' + encodeURIComponent(adjRow.customer));
      setAdjItems(j.adjustments || []);
      // Refresh Balances table to reflect new total
      bustPrefix('/api/balances/recon');
      reload();
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setAdjBusy(false); }
  }
  // Backdate an existing adjustment. New YYYY-MM-DD lands as
  // posted_at=<date>T12:00:00Z so it sits mid-day in whatever local
  // timezone reads it back. Suresh 2026-07-08: had two adjustments
  // to re-date after the picker was added.
  async function updateAdjustmentDate(id, newDate) {
    if (!newDate || !/^\d{4}-\d{2}-\d{2}$/.test(newDate)) return;
    setAdjBusy(true);
    try {
      const resp = await fetch('/api/balance-adjustments/' + id, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ posted_at: newDate + 'T12:00:00Z' })
      });
      if (!resp.ok) throw new Error('HTTP ' + resp.status + ': ' + await resp.text());
      // Query by DATE RANGE, not cycle_key. Pre-schema-032 adjustments
      // have stale cycle_key values (defaulted from the row's original
      // cycle at insert time) even though their posted_at now lines up
      // with a different reconciliation window. The Balances tab uses
      // posted_at via balances-recon; the modal must use the same
      // window to show the same set of items. Suresh 2026-07-08.
      const j = await fetchJson('/api/balance-adjustments?from=' + encodeURIComponent(from)
                              + '&to=' + encodeURIComponent(to)
                              + '&customer=' + encodeURIComponent(adjRow.customer));
      setAdjItems(j.adjustments || []);
      bustPrefix('/api/balances/recon');
      reload();
    } catch (e) { alert('Date update failed: ' + e.message); }
    finally { setAdjBusy(false); }
  }
  async function deleteAdjustment(id) {
    if (!confirm('Delete this adjustment?')) return;
    setAdjBusy(true);
    try {
      await fetch('/api/balance-adjustments/' + id, { method: 'DELETE', credentials: 'include' });
      // Query by DATE RANGE, not cycle_key. Pre-schema-032 adjustments
      // have stale cycle_key values (defaulted from the row's original
      // cycle at insert time) even though their posted_at now lines up
      // with a different reconciliation window. The Balances tab uses
      // posted_at via balances-recon; the modal must use the same
      // window to show the same set of items. Suresh 2026-07-08.
      const j = await fetchJson('/api/balance-adjustments?from=' + encodeURIComponent(from)
                              + '&to=' + encodeURIComponent(to)
                              + '&customer=' + encodeURIComponent(adjRow.customer));
      setAdjItems(j.adjustments || []);
      bustPrefix('/api/balances/recon');
      reload();
    } catch (e) { alert('Delete failed: ' + e.message); }
    finally { setAdjBusy(false); }
  }
  // ── Loading the balances for the period (with guards against a late reply for an old period) ──
  useEffect(() => { fetchJson('/api/whoami').then(setMe).catch(() => setMe(null)); }, []);

  const cycleKey = cycleKeyFromDate(from);

  // Race-condition guard 2026-07-07: when the user switches cycles, the
  // `data` state briefly still holds the previous cycle's rows +
  // footer totals until the new cycle's fetch resolves. That renders
  // WRONG-CYCLE totals under the new cycle's title — Suresh's report.
  // Clear the cached response the moment the cycle key changes so the
  // table shows a loading state instead of stale totals. Reload button
  // path is unaffected (cycleKey doesn't change, useEffect doesn't
  // fire).
  useEffect(() => { setData(null); }, [cycleKey]);

  // Token-based guard against stale onUpdate callbacks. `cachedFetch`
  // is stale-while-revalidate: it returns the cached value immediately
  // AND fires `onUpdate(fresh)` when its background refresh completes.
  // Without a guard, the onUpdate closure from a PREVIOUS load() (e.g.
  // for the old cycle URL) can setData with old-cycle data AFTER the
  // user has switched cycles -- causing the TOTALS/rows to flip back
  // to the wrong cycle even though the period label is now correct.
  // Suresh 2026-07-08: kept happening intermittently on cycle switch
  // even after the setData(null) guard was added, because that guard
  // only clears state at switch time -- it doesn't stop a late-arriving
  // background refresh from re-writing it.
  //
  // Each load() call bumps loadTokenRef; the callbacks check they're
  // still the current call before touching state. Same pattern api.js
  // useApi already uses.
  const loadTokenRef = useRef(0);
  const load = useCallback(async (force) => {
    const myToken = ++loadTokenRef.current;
    const isCurrent = () => loadTokenRef.current === myToken;
    setLoading(true); setErr(null);
    try {
      const qs = '/api/balances/recon?from=' + from + '&to=' + to
              + (fromTime ? '&fromTime=' + encodeURIComponent(fromTime) : '')
              + (toTime   ? '&toTime='   + encodeURIComponent(toTime)   : '')
              + (force === true ? '&nocache=1' : '');   // Reload = fresh from QuickBooks
      const r = await cachedFetch(qs, {
        ttl: 30_000,
        onUpdate: (fresh) => { if (isCurrent()) setData(fresh); },
        onRefreshStart: () => { if (isCurrent()) setRefreshing(true); },
        onRefreshEnd: () => { if (isCurrent()) setRefreshing(false); }
      });
      if (isCurrent()) setData(r);
    } catch (e) { if (isCurrent()) setErr(e.message); }
    finally { if (isCurrent()) setLoading(false); }
  }, [from, to, fromTime, toTime]);
  // reload() forces a fresh fetch (busts the cache first). Used by realtime
  // handlers, the Reload button, and post-mutation paths — anywhere we know
  // the cached value is potentially stale.
  const reload = useCallback(() => {
    bustPrefix('/api/balances/recon');
    return load(true);
  }, [load]);
  useEffect(() => { load(); }, [load]);

  // ── QuickBooks reconnect, cycle status, reconciliation loading, close cycle ──
  // QBO reconnect — opens the OAuth authorization URL in a new tab.
  // After authorizing, the dashboard's /api/qbo-callback stores fresh tokens.
  const [qboReconnectMsg, setQboReconnectMsg] = useState(null);
  async function handleQboReconnect() {
    // Same-tab redirect. The callback page shows a Back-to-dashboard link
    // after success. No popups — they're unreliable across browsers due
    // to popup blockers + cross-window permission rules.
    setQboReconnectMsg('Starting QBO reconnect…');
    try {
      const r = await fetchJson('/api/qbo-connect');
      if (!r || !r.authUrl) throw new Error('No authUrl returned by /api/qbo-connect');
      window.location.href = r.authUrl;
    } catch (ex) {
      setQboReconnectMsg('Reconnect failed: ' + ex.message);
    }
  }

  // Pull (and ensure) the statement_cycles row for this cycle so we know status
  useEffect(() => {
    if (!cycleKey) { setCycleInfo(null); return; }
    let alive = true;
    postJson('/api/cycles/ensure', { cycle_key: cycleKey })
      .then(j => { if (alive) setCycleInfo(j && j.cycle); })
      .catch(() => { if (alive) setCycleInfo(null); });
    return () => { alive = false; };
  }, [cycleKey]);

  // Lazily load reconciliation when the panel is opened
  useEffect(() => {
    if (!cycleKey || !reconOpen) return;
    let alive = true;
    setReconBusy(true);
    cachedFetch('/api/cycle-reconcile?cycle=' + encodeURIComponent(cycleKey), { ttl: 30_000, onUpdate: setRecon })
      .then(j => { if (alive) setRecon(j); })
      .catch(e => { if (alive) setRecon({ error: e.message }); })
      .finally(() => { if (alive) setReconBusy(false); });
    return () => { alive = false; };
  }, [cycleKey, reconOpen]);

  async function confirmClose() {
    if (!cycleKey) return;
    setCloseBusy(true);
    try {
      const r = await postJson('/api/cycles/close', { cycle_key: cycleKey, notes: closeNotes.trim() || null });
      setCycleInfo(prev => prev ? { ...prev, status: 'closed', closed_at: new Date().toISOString(), closed_by: (me && me.userDetails) || 'you' } : prev);
      setCloseOpen(false);
      setCloseNotes('');
      bustPrefix('/api/balances/recon');
      bustPrefix('/api/cycle-reconcile');
      load();
      alert('Cycle ' + cycleKey + ' closed. Snapshotted ' + (r.snapshotCustomers || 0) + ' customer balances.');
    } catch (e) {
      alert('Close failed: ' + e.message);
    } finally { setCloseBusy(false); }
  }

  // ── Period navigation: previous / next / current cycle, custom range ──
  function jumpPrev() {
    const d = new Date(from + 'T12:00:00');
    if (d.getDate() === 16) setRange({ from: iso(d.getFullYear(), d.getMonth(), 1), to: iso(d.getFullYear(), d.getMonth(), 15) });
    else {
      const prev = new Date(d.getFullYear(), d.getMonth() - 1, 16);
      const lastDay = new Date(prev.getFullYear(), prev.getMonth() + 1, 0).getDate();
      setRange({ from: iso(prev.getFullYear(), prev.getMonth(), 16), to: iso(prev.getFullYear(), prev.getMonth(), lastDay) });
    }
    clearCustomTimes();
  }
  function jumpNext() {
    const d = new Date(from + 'T12:00:00');
    if (d.getDate() === 1) setRange({ from: iso(d.getFullYear(), d.getMonth(), 16), to: iso(d.getFullYear(), d.getMonth(), new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()) });
    else {
      const nxt = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      setRange({ from: iso(nxt.getFullYear(), nxt.getMonth(), 1), to: iso(nxt.getFullYear(), nxt.getMonth(), 15) });
    }
    clearCustomTimes();
  }
  function jumpCycle() { setRange(currentCycle()); clearCustomTimes(); }
  function clearCustomTimes() { setFromTime(''); setToTime(''); }
  function applyCustomRange() {
    if (!customFrom || !customTo) { alert('From and To dates are required.'); return; }
    if (customFrom > customTo) { alert('From date must be on or before To date.'); return; }
    setRange({ from: customFrom, to: customTo });
    setFromTime(customFromTime || '');
    setToTime(customToTime || '');
    setCustomOpen(false);
  }

  const customers = (data && data.customers) || [];

  // ── Billing groups: parent customers + everyone billed to them ──
  // Compute billing-group rollup: each parent customer's group total = its
  // own ending balance + the ending balance of every customer billed to it.
  const billingRollup = (() => {
    const parents = new Map();  // parentName(lower) -> { displayName, parentEnding, children: [{customer, ending}] }
    const allByName = new Map();
    for (const c of customers) allByName.set(String(c.customer || '').toLowerCase(), c);
    for (const c of customers) {
      if (!c.billedTo) continue;
      const key = String(c.billedTo).toLowerCase();
      const parentRow = allByName.get(key);
      const parentEnding = parentRow ? (parentRow.endingWithPending != null ? parentRow.endingWithPending : parentRow.ending) || 0 : 0;
      const childEnding = (c.endingWithPending != null ? c.endingWithPending : c.ending) || 0;
      const display = parentRow ? parentRow.customer : c.billedTo;
      const cur = parents.get(key) || { displayName: display, parentEnding, children: [] };
      cur.children.push({ customer: c.customer, ending: childEnding });
      parents.set(key, cur);
    }
    return Array.from(parents.entries())
      .map(([k, v]) => ({ ...v,
        groupTotal: (v.parentEnding || 0) + v.children.reduce((s, x) => s + x.ending, 0)
      }))
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  })();
  const childrenSet = new Set();   // customer keys that are children of some parent
  for (const g of billingRollup) for (const c of g.children) childrenSet.add(String(c.customer || '').toLowerCase());
  const parentsSet = new Set();
  for (const g of billingRollup) parentsSet.add(String(g.displayName || '').toLowerCase());

  // ── Live refresh when tips change (debounced) ──
  // Realtime — when a tip is approved / corrected / inserted on tips_google,
  // re-fetch the reconciliation.
  //
  // 2026-07-08: the subscription callback previously closed over `reload`
  // captured from the FIRST render. `reload` depends on `load` depends on
  // `from`/`to`, so after any cycle switch the callback was calling a
  // STALE reload that re-fetched the OLD cycle's URL and setData'd the
  // wrong period into the current view. When the Google Sheet sync
  // inserted pending rows periodically, the tab would flicker back to
  // the prior cycle's totals -- exactly the "refreshed for some reason"
  // Suresh described. Fix: keep a ref pointing at the LATEST reload
  // and dispatch through it. The subscription itself still lives for
  // the mount lifetime, so we don't churn Supabase channels on every
  // cycle switch.
  const reloadRef = useRef(reload);
  useEffect(() => { reloadRef.current = reload; }, [reload]);
  // Debounce Realtime-triggered reloads. Scaling audit 2026-07-08 (QW5):
  // an operator bulk-approving 20 pending rows in the Tips inbox fires
  // 20 UPDATE events on tips_excel, each of which triggered a full
  // balances-recon refetch here. At 5-10 concurrent viewers this
  // thrashes the L1 cache and burns Supabase Realtime message quota.
  // 500ms debounce coalesces bursts into a single refetch. Sync
  // updates (single operator edit) still feel instant.
  const debounceTimerRef = useRef(null);
  useEffect(() => {
    const scheduleReload = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        reloadRef.current();
      }, 500);
    };
    const channel = supabase
      .channel('balances_tips_google')
      // signal row bumped by a DB trigger on tip changes (no tip data via the public key — 2026-10-07)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'realtime_signals', filter: 'name=eq.tips' }, scheduleReload)
      .subscribe();
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      supabase.removeChannel(channel);
    };
  }, []);
  const totals = (data && data.totals) || {};
  const checks = (data && data.checks) || {};

  // ── Screen: header, sub-tabs, then (Balances tab) reminders, period bar, table, reconciliation and pop-ups ──
  return (
    <div className="page">
      {/* Page title, cycle buttons (prev / current / next) and Reload. */}
      <PageHeader canWrite={canWrite} customers={customers} cycleKey={cycleKey} from={from} jumpCycle={jumpCycle} jumpNext={jumpNext} jumpPrev={jumpPrev} loading={loading} refreshing={refreshing} reload={reload} to={to} />

      {/* Sub-tabs: Balances | Compliance certificates | Send log. */}
      <SubTabs setSubTab={setSubTab} subTab={subTab} />

      {subTab === 'certificates' && (
        <CertificatesPanel canWrite={canWrite} isAdmin={!!(me && me.isAdmin)} cycleKey={cycleKey} forceOpen />
      )}

      {subTab === 'sendlog' && <SendLogPanel cycleKey={cycleKey} />}

      {subTab === 'balances' && (<>

      {/* Reminder: customers in this period with Google-form tips still pending review (these block statement sending). */}
      <PendingReminder customers={customers} data={data} from={from} recon={recon} to={to} totals={totals} />

      {/* Big warning when QuickBooks could not be reached (invoices / payments missing from the balances). */}
      <QboWarning data={data} from={from} handleQboReconnect={handleQboReconnect} qboReconnectMsg={qboReconnectMsg} recon={recon} to={to} totals={totals} />

      {/* Period bar: cycle name, prev / current / next, and the period totals (tips, pending, invoiced, other, adjustments, variance, ending). */}
      <CycleBar canWrite={canWrite} customOpen={customOpen} customers={customers} cycleKey={cycleKey} from={from} fromTime={fromTime} jumpCycle={jumpCycle} jumpNext={jumpNext} jumpPrev={jumpPrev} loading={loading} openStatementsModal={openStatementsModal} setCustomOpen={setCustomOpen} to={to} toTime={toTime} totals={totals} />

      {/* Custom date + time range picker. */}
      <CustomRangeBar applyCustomRange={applyCustomRange} clearCustomTimes={clearCustomTimes} customFrom={customFrom} customFromTime={customFromTime} customOpen={customOpen} customTo={customTo} customToTime={customToTime} jumpCycle={jumpCycle} loading={loading} setCustomFrom={setCustomFrom} setCustomFromTime={setCustomFromTime} setCustomOpen={setCustomOpen} setCustomTo={setCustomTo} setCustomToTime={setCustomToTime} to={to} />

      {err && <div className="card" style={{ padding: '0.6rem 1rem', marginBottom: '0.5rem', background: '#fef2f2', color: '#991b1b' }}>{err}</div>}

      {/* The balances table: one row per customer - opening balance, loads billed, invoices, payments, adjustments, ending balance, billing group totals. */}
      <BalancesTable billingRollup={billingRollup} canWrite={canWrite} checks={checks} childrenSet={childrenSet} customers={customers} cycleKey={cycleKey} data={data} drill={drill} from={from} load={load} loading={loading} openAdjustmentsModal={openAdjustmentsModal} openExplain={(c) => setExplain({ customer: c })} parentsSet={parentsSet} setDrill={setDrill} to={to} totals={totals} />

      {/* Cycle reconciliation (show / hide): tips vs invoices check for the period. */}
      <ReconPanel customers={customers} from={from} loading={loading} recon={recon} reconBusy={reconBusy} reconOpen={reconOpen} setReconOpen={setReconOpen} to={to} totals={totals} />

      {/* Pop-up: generate / preview / email customer statements for the period (with standing CC). */}
      <StatementsModal canWrite={canWrite} customers={customers} cycleKey={cycleKey} from={from} openStatementsModal={openStatementsModal} saveStmtCc={saveStmtCc} setStmtCc={setStmtCc} setStmtsBusy={setStmtsBusy} setStmtsOpen={setStmtsOpen} stmtCc={stmtCc} stmtCcBusy={stmtCcBusy} stmtCcSaved={stmtCcSaved} stmtRange={stmtRange} stmtsBusy={stmtsBusy} stmtsList={stmtsList} stmtsOpen={stmtsOpen} to={to} />

      {/* Pop-up: close the statement cycle (freezes the numbers). */}
      <CloseCycleModal closeBusy={closeBusy} closeNotes={closeNotes} closeOpen={closeOpen} confirmClose={confirmClose} customers={customers} cycleKey={cycleKey} from={from} setCloseNotes={setCloseNotes} setCloseOpen={setCloseOpen} to={to} />

      {/* Pop-up: the tips or invoices behind one customer's number. */}
      <DrillModal drill={drill} setDrill={setDrill} />
      <ExplainModal explain={explain} setExplain={setExplain} />
      {/* Pop-up: balance adjustments for one customer (add, re-date, delete). */}
      <AdjustmentsModal adjAmount={adjAmount} adjBusy={adjBusy} adjDate={adjDate} adjDescription={adjDescription} adjItems={adjItems} adjRow={adjRow} cycleKey={cycleKey} deleteAdjustment={deleteAdjustment} saveAdjustment={saveAdjustment} setAdjAmount={setAdjAmount} setAdjDate={setAdjDate} setAdjDescription={setAdjDescription} setAdjRow={setAdjRow} to={to} updateAdjustmentDate={updateAdjustmentDate} />
      </>)}
    </div>
  );
}
